/**
 * Hook for fast, synchronous user role detection.
 *
 * Reads the user_role cookie set by middleware (proxy.ts) on every request.
 * This is faster than useSession() because:
 * - Cookie is available immediately on page load (no async fetch)
 * - Middleware sets it server-side before the page renders
 *
 * IMPORTANT: Use this ONLY for role checks (isAdmin, isPartner), NOT for
 * authentication checks. For authentication, use useSession() from next-auth.
 *
 * Usage:
 *   const { role, isAdmin, isPartner, isUser } = useUserRole();
 */

import { useSyncExternalStore } from "react";

type UserRole = "admin" | "partner" | "user" | null;

function getCookieRole(): UserRole {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(/(?:^|; )user_role=([^;]*)/);
  if (match) {
    const role = match[1];
    if (role === "admin" || role === "partner" || role === "user") {
      return role;
    }
  }
  return null;
}

// For SSR, return null (cookie not available server-side in this context)
function getServerSnapshot(): UserRole {
  return null;
}

// Subscribe to cookie changes (cookies don't have native events, so this is a no-op)
// The cookie is set by middleware on navigation, so the component will re-render anyway
function subscribe(callback: () => void): () => void {
  // No-op: cookies don't emit change events
  // Role changes happen on navigation (middleware sets cookie), which triggers re-render
  return () => {};
}

export function useUserRole() {
  const role = useSyncExternalStore(subscribe, getCookieRole, getServerSnapshot);

  return {
    role,
    /** True only after role is resolved and equals "admin" */
    isAdmin: role === "admin",
    isPartner: role === "partner",
    isUser: role === "user",
    /** True when role hasn't been resolved yet (SSR or cookie not read) */
    isLoading: role === null,
  };
}
